/**
 * DH Parameters and specifications for Universal Robots
 *
 * Using Modified DH Convention:
 * - d: offset along previous z to the common normal
 * - a: length of the common normal
 * - alpha: angle about common normal, from old z axis to new z axis
 * - theta: angle about previous z, from old x to new x
 *
 * References:
 * - Kelsey P. Hawkins (2013): "Analytic Inverse Kinematics for the Universal Robots UR-5/UR-10 Arms"
 * - Universal Robots URe-Series Technical Specifications
 */

export const UR_ROBOTS = {
  UR10e: {
    name: 'UR10e',
    description: 'Universal Robots UR10e Collaborative Robot',
    // DH Parameters (Modified DH Convention) in meters
    dh: {
      d1: 0.1807,    // Base to shoulder joint (along z0)
      a2: -0.6127,   // Shoulder to elbow (along x1, negative because it points backward)
      a3: -0.57155,  // Elbow to wrist 1 (along x2, negative because it points backward)
      d4: 0.17415,   // Wrist 1 offset (along z3)
      d5: 0.11985,   // Wrist 2 offset (along z4)
      d6: 0.11655,   // Wrist 3 to tool flange (along z5)
    },
    // Default TCP offset (Tool Center Point relative to flange)
    defaultTCP: {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    },
    // Joint limits in radians
    jointLimits: [
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Base (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Shoulder (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Elbow (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 1 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 2 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 3 (continuous)
    ],
    // Workspace
    reach: 1.300,  // meters
    payload: 12.5, // kg
  },

  UR20: {
    name: 'UR20',
    description: 'Universal Robots UR20 Collaborative Robot',
    // DH Parameters (Modified DH Convention) in meters
    // Source: https://www.universal-robots.com/articles/ur/application-installation/dh-parameters-for-calculations-of-kinematics-and-dynamics/
    dh: {
      d1: 0.2363,    // Base to shoulder joint (along z0) - 236.3mm
      a2: -0.8620,   // Shoulder to elbow (along x1) - 862.0mm
      a3: -0.7287,   // Elbow to wrist 1 (along x2) - 728.7mm
      d4: 0.2010,    // Wrist 1 offset (along z3) - 201.0mm
      d5: 0.1593,    // Wrist 2 offset (along z4) - 159.3mm
      d6: 0.1543,    // Wrist 3 to tool flange (along z5) - 154.3mm
    },
    // Default TCP offset (Tool Center Point relative to flange)
    defaultTCP: {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    },
    // Joint limits in radians
    jointLimits: [
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Base (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Shoulder (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Elbow (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 1 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 2 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 3 (continuous)
    ],
    // Workspace
    reach: 1.750,  // meters
    payload: 20.0, // kg
  },

  UR5e: {
    name: 'UR5e',
    description: 'Universal Robots UR5e Collaborative Robot',
    // DH Parameters (Modified DH Convention) in meters
    dh: {
      d1: 0.1625,    // Base to shoulder joint (along z0) - 162.5mm
      a2: -0.425,    // Shoulder to elbow (along x1) - 425mm
      a3: -0.3922,   // Elbow to wrist 1 (along x2) - 392.2mm
      d4: 0.1333,    // Wrist 1 offset (along z3) - 133.3mm
      d5: 0.0997,    // Wrist 2 offset (along z4) - 99.7mm
      d6: 0.0996,    // Wrist 3 to tool flange (along z5) - 99.6mm
    },
    // Default TCP offset (Tool Center Point relative to flange)
    defaultTCP: {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    },
    // Joint limits in radians
    jointLimits: [
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Base (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Shoulder (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Elbow (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 1 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 2 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 3 (continuous)
    ],
    // Workspace
    reach: 0.850,  // meters
    payload: 5.0,  // kg
  },

  UR8long: {
    name: 'UR8long',
    description: 'Universal Robots UR8 Long Reach Collaborative Robot',
    // DH Parameters (Modified DH Convention) in meters
    dh: {
      d1: 0.2186,    // Base to shoulder joint (along z0) - 218.6mm
      a2: -0.8989,   // Shoulder to elbow (along x1) - 898.9mm
      a3: -0.7149,   // Elbow to wrist 1 (along x2) - 714.9mm
      d4: 0.1824,    // Wrist 1 offset (along z3) - 182.4mm
      d5: 0.1361,    // Wrist 2 offset (along z4) - 136.1mm
      d6: 0.1434,    // Wrist 3 to tool flange (along z5) - 143.4mm
    },
    // Default TCP offset (Tool Center Point relative to flange)
    defaultTCP: {
      x: 0.002880472034598727,
      y: -0.02918177960863152,
      z: 0.47236807083603005,
      rx: -0.8846974644582652,
      ry: 0.010026373014888897,
      rz: -0.005155957771068098
    },
    // Joint limits in radians
    jointLimits: [
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Base (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Shoulder (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Elbow (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 1 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 2 (continuous)
      { min: -2 * Math.PI, max: 2 * Math.PI },  // Wrist 3 (continuous)
    ],
    // Workspace
    reach: 1.614,  // meters (calculated from a2 + a3)
    payload: 8.0,  // kg
  },
};

/**
 * Get robot parameters by name
 * @param {string} robotName - Name of the robot (e.g., 'UR10e', 'UR20')
 * @returns {Object} Robot parameters
 */
export function getRobotParameters(robotName) {
  const robot = UR_ROBOTS[robotName];
  if (!robot) {
    throw new Error(`Unknown robot: ${robotName}. Available: ${Object.keys(UR_ROBOTS).join(', ')}`);
  }
  return robot;
}

/**
 * List all available robot models
 * @returns {Array<string>} Array of robot model names
 */
export function getAvailableRobots() {
  return Object.keys(UR_ROBOTS);
}
